/**
 * localStorage persistence middleware for product-grid2.
 *
 * This module integrates the product grid with the configurator's preference system,
 * allowing user selections to be persisted across page loads.
 *
 * ## Persistence Strategy
 *
 * - Product option selections (size, color, etc.) are saved to localStorage
 * - Filter selections that represent product options are also persisted
 * - Only selections that result in valid product configurations are saved
 * - Persistence happens synchronously when actions are dispatched
 *
 * The middleware intercepts specific actions and saves their values to localStorage
 * using the shared configurator persistence utilities. This allows product grids
 * to remember user preferences even when they navigate away and come back.
 *
 * @see ../configurator/persistence.ts for the underlying storage implementation
 */
import { Middleware } from "@reduxjs/toolkit";

// Integrate with the configurator's preference system
import {
    getPreferredOptionSelections,
    persistOptionValue,
} from "../configurator/persistence";
import {
    setOptionValue,
    updateSelectManyFilterValue,
    updateSelectOneFilterValue,
} from "./reducers";

export { getPreferredOptionSelections };

/**
 * Redux middleware that persists option selection values to localStorage.
 *
 * This middleware intercepts actions that change product options or filter values
 * and saves them to localStorage so they can be automatically restored on the next page load.
 *
 * Persisted actions:
 * - setOptionValue: Direct product option changes
 * - updateSelectOneFilterValue: Single-select filter changes (when a value is selected)
 * - updateSelectManyFilterValue: Multi-select filter changes (when a value is added)
 */
export const persistOptionSelections: Middleware = () => {
    return (next) => {
        return (action) => {
            if (setOptionValue.match(action)) {
                persistOptionValue(
                    action.payload.namespace,
                    action.payload.code,
                    action.payload.value,
                );
            }
            if (
                updateSelectOneFilterValue.match(action) &&
                action.payload.optionID
            ) {
                persistOptionValue(
                    action.payload.optionNamespace,
                    action.payload.filterID,
                    action.payload.optionID,
                );
            }
            if (
                updateSelectManyFilterValue.match(action) &&
                action.payload.isSelected
            ) {
                persistOptionValue(
                    action.payload.optionNamespace,
                    action.payload.filterID,
                    action.payload.optionID,
                );
            }
            return next(action);
        };
    };
};
